import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import useOutsideClick from "../hooks/useOutsideClick";

export default function ActionPopup({
    onClose,
    items,
    toggleRef,
    containerRef,
    style,
    className,
}) {
    const popupRef = useRef(null);
    const isClickedOutside = useOutsideClick(popupRef, toggleRef);
    const [rawPosition, setRawPosition] = useState(null);
    const [position, setPosition] = useState(null);

    useEffect(() => {
        if (isClickedOutside) {
            onClose();
        }
    }, [isClickedOutside, onClose]);

    useLayoutEffect(() => {
        const updateRawPosition = () => {
            const trigger = toggleRef?.current;
            if (!trigger) return;
            const rect = trigger.getBoundingClientRect();
            setRawPosition({ top: rect.bottom, left: rect.left });
        };

        updateRawPosition();
        window.addEventListener("scroll", updateRawPosition, true);
        window.addEventListener("resize", updateRawPosition);

        return () => {
            window.removeEventListener("scroll", updateRawPosition, true);
            window.removeEventListener("resize", updateRawPosition);
        };
    }, [toggleRef]);

    useLayoutEffect(() => {
        if (!rawPosition) return;

        const container = containerRef?.current;
        const popup = popupRef.current;

        if (!container || !popup) {
            setPosition(rawPosition);
            return;
        }

        const containerRect = container.getBoundingClientRect();
        const popupRect = popup.getBoundingClientRect();

        const minLeft = containerRect.left;
        const maxLeft = Math.max(
            minLeft,
            containerRect.right - popupRect.width
        );
        const minTop = containerRect.top;
        const maxTop = Math.max(
            minTop,
            containerRect.bottom - popupRect.height
        );

        setPosition({
            top: Math.min(Math.max(rawPosition.top, minTop), maxTop),
            left: Math.min(Math.max(rawPosition.left, minLeft), maxLeft),
        });
    }, [rawPosition, containerRef]);

    const visibleItems = items.filter((item) => item.visible !== false);

    if (!rawPosition) {
        return null;
    }

    return ReactDOM.createPortal(
        <div
            className={
                "section-card action-popup" + (className ? " " + className : "")
            }
            style={{
                ...style,
                position: "fixed",
                top: (position ?? rawPosition).top,
                left: (position ?? rawPosition).left,
            }}
            ref={popupRef}
        >
            {visibleItems.map((item, index) => (
                <button
                    key={item.key ?? index}
                    type="button"
                    className={
                        "cursor-pointer" +
                        (item.className ? " " + item.className : "")
                    }
                    onClick={item.onClick}
                >
                    {item.icon}
                    {item.label}
                </button>
            ))}
        </div>,
        document.body
    );
}

ActionPopup.propTypes = {
    onClose: PropTypes.func.isRequired,
    items: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.node.isRequired,
            icon: PropTypes.node,
            onClick: PropTypes.func.isRequired,
            visible: PropTypes.bool,
            className: PropTypes.string,
            key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })
    ).isRequired,
    toggleRef: PropTypes.shape({
        current: PropTypes.any,
    }),
    containerRef: PropTypes.shape({
        current: PropTypes.any,
    }),
    style: PropTypes.object,
    className: PropTypes.string,
};

ActionPopup.defaultProps = {
    toggleRef: undefined,
    containerRef: undefined,
    style: undefined,
    className: "",
};
